/**
 * UpcomingPaymentsTile — Dashboard side-tile that shows the next-due
 * promised payments across all clients.
 *
 * Pulls from GET /api/promised-payments/upcoming?days=14 and renders:
 *   • Empty state: "No promised payments coming up"
 *   • Tile: client name + amount + relative day (Today / Tomorrow / Friday / Jul 12)
 *
 * Placed in the dashboard via:
 *   <UpcomingPaymentsTile />
 *
 * Auto-refreshes every 60 seconds so the user sees current data without
 * a manual reload.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  return v === Math.round(v) ? `$${v.toLocaleString()}` : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const relativeDay = (iso, daysUntil) => {
  if (daysUntil === 0) return "Today";
  if (daysUntil === 1) return "Tomorrow";
  if (daysUntil <= 6) {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long" });
  }
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export default function UpcomingPaymentsTile() {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetchUpcoming = async () => {
      try {
        const r = await axios.get(`${API}/api/promised-payments/upcoming?days=14`);
        if (alive) {
          setRows(r.data?.rows || []);
          setLoaded(true);
        }
      } catch (e) {
        if (alive) setLoaded(true);
      }
    };
    fetchUpcoming();
    const t = setInterval(fetchUpcoming, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!loaded || rows.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-40 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-emerald-500/40 bg-zinc-900/95 backdrop-blur shadow-xl shadow-emerald-900/20 overflow-hidden"
      data-testid="upcoming-payments-tile"
    >
      <div className="px-4 py-2.5 flex items-center justify-between bg-emerald-500/15 border-b border-emerald-500/30">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-emerald-400" />
          <h4 className="text-xs font-bold text-emerald-200 uppercase tracking-wider">
            Upcoming Payments
          </h4>
        </div>
        <span className="text-[10px] text-emerald-300 font-semibold bg-emerald-500/20 px-2 py-0.5 rounded-full">
          {rows.length}
        </span>
      </div>
      <ul className="divide-y divide-zinc-800 max-h-72 overflow-y-auto">
        {rows.slice(0, 8).map((r) => (
          <li
            key={r.plan_id + r.next_due}
            className="px-4 py-2 flex items-center justify-between hover:bg-zinc-800/60 transition-colors"
            data-testid={`upcoming-${r.plan_id}`}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{r.client_name}</p>
              <p className="text-[10px] text-zinc-500">
                {r.mode === "weekly" ? `Weekly · ${r.weekly_day_name}` : "Custom date"}
              </p>
            </div>
            <div className="text-right ml-3">
              <p className="text-sm font-bold text-emerald-300">{fmtMoney(r.amount)}</p>
              <p className={`text-[10px] font-semibold ${r.days_until === 0 ? "text-amber-300" : r.days_until <= 2 ? "text-orange-300" : "text-zinc-400"}`}>
                {relativeDay(r.next_due, r.days_until)}
              </p>
            </div>
          </li>
        ))}
        {rows.length > 8 && (
          <li className="px-4 py-2 text-center text-[11px] text-zinc-500 flex items-center justify-center gap-1">
            <ChevronRight className="w-3 h-3" />
            and {rows.length - 8} more in the next 14 days
          </li>
        )}
      </ul>
    </div>
  );
}
